import type { ChangeEvent, CSSProperties, FC } from "react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@9amhealth/shared";
import { IconCrossXCloseMissed } from "src/constants/icons";
import translate from "src/lib/translate";
import Translate from "src/ui/components/Translate/Translate";
import { z } from "zod";

import { SubmitSpinner } from "./MealRefineBar.styled";
import {
  Actions,
  Body,
  CloseButton,
  ConfirmLabel,
  CorrectionInput,
  FieldError,
  Header,
  HeaderSpacer,
  RefineModal,
  Title
} from "./MealRefineDialog.styled";

const MAX_CORRECTION_LENGTH = 500;

const MODAL_STYLE = {
  "--max-width": "500px"
} as CSSProperties;

const CorrectionSchema = z
  .string({ error: translate("error_validation_required") })
  .trim()
  .min(1, translate("error_validation_required"))
  .max(MAX_CORRECTION_LENGTH);

interface MealRefineDialogProps {
  onSubmit: (correction: string) => Promise<boolean>;
  onCancel: () => void;
}

/**
 * Mouse and keyboard: the chat-style refine bar reads as a mobile composer, so
 * the correction is typed in the same alert dialog shape the rest of the app
 * uses for a decision.
 */
const MealRefineDialog: FC<MealRefineDialogProps> = ({
  onSubmit,
  onCancel
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [correction, setCorrection] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    setCorrection(event.target.value.slice(0, MAX_CORRECTION_LENGTH));
    setError("");
  };

  const handleClose = (): void => {
    if (pending) {
      return;
    }
    setIsOpen(false);
  };

  const handleConfirm = async (): Promise<void> => {
    if (pending) {
      return;
    }

    const parsed = CorrectionSchema.safeParse(correction);
    if (!parsed.success) {
      setError(parsed.error.issues[0].message);
      inputRef.current?.focus();
      return;
    }

    setPending(true);
    const refined = await onSubmit(parsed.data);
    if (refined) {
      setIsOpen(false);
    } else {
      setPending(false);
    }
  };

  return (
    <RefineModal
      isOpen={isOpen}
      variant="alert"
      style={MODAL_STYLE}
      ariaLabel={translate("mealInsights.refine.dialogTitle")}
      onWillDismiss={handleClose}
      onDidDismiss={onCancel}
    >
      <Header>
        <HeaderSpacer />
        <Title>
          <Translate msg="mealInsights.refine.dialogTitle" />
        </Title>
        <CloseButton
          type="button"
          aria-label={translate("close")}
          onClick={handleClose}
        >
          <IconCrossXCloseMissed />
        </CloseButton>
      </Header>

      <Body>
        <CorrectionInput
          ref={inputRef}
          rows={4}
          value={correction}
          maxLength={MAX_CORRECTION_LENGTH}
          disabled={pending}
          data-invalid={Boolean(error)}
          aria-invalid={Boolean(error)}
          aria-errormessage={error ? "meal-refine-error" : undefined}
          aria-label={translate("mealInsights.refine.inputLabel")}
          placeholder={translate("mealInsights.refine.placeholder")}
          onChange={handleChange}
        />
        {error && <FieldError id="meal-refine-error">{error}</FieldError>}

        <Actions>
          <Button
            theme="simple"
            hideArrow
            isDisabled={pending}
            onPress={handleClose}
          >
            <Translate msg="mealInsights.refine.discard" />
          </Button>
          <Button
            theme="charcoal"
            hideArrow
            isDisabled={pending}
            aria-label={
              pending ? translate("mealInsights.refine.pending") : undefined
            }
            onPress={() => void handleConfirm()}
          >
            <ConfirmLabel>
              <Translate msg="mealInsights.refine.confirm" />
              {pending && <SubmitSpinner />}
            </ConfirmLabel>
          </Button>
        </Actions>
      </Body>
    </RefineModal>
  );
};

export default MealRefineDialog;
